/**
 * Scoring + disqualification → outcome resolution.
 *
 * Disqualification does NOT reject inline (PLAN.md §8.5). The user completes
 * the whole funnel; every matched `dq_rule`'s reason accumulates; the result
 * step resolves the outcome:
 *   - any DQ matched              → outcome "disqualified"
 *   - else, score-band tier from total score
 */
import type { Answers, FunnelConfig, Outcome, Tier } from "../types";
import { matchGroup } from "./conditions";

export const DISQUALIFIED = "disqualified";

/** Sum the points of every score rule whose conditions match. */
export function computeScore(config: FunnelConfig, answers: Answers): number {
  let score = 0;
  for (const rule of config.scoring.score_rules ?? []) {
    if (matchGroup(rule.conditions, answers)) score += rule.points;
  }
  return score;
}

/** Human-readable REASON STRINGS from every matched DQ rule (UI rendering). */
export function collectDqReasons(config: FunnelConfig, answers: Answers): string[] {
  const reasons: string[] = [];
  for (const rule of config.scoring.dq_rules) {
    if (matchGroup(rule.conditions, answers)) reasons.push(rule.reason);
  }
  return reasons;
}

/** RULE IDs (e.g. "dq_attorney") from every matched DQ rule (analytics / wire).
 *  Per LD-4: this is what gets serialized into `lead_ledger.dq_reason` JSON-array.
 *  Stable across reason-text edits — operators can rewrite the consumer-facing
 *  reason text without breaking downstream analytics queries. */
export function collectDqRuleIds(config: FunnelConfig, answers: Answers): string[] {
  const ids: string[] = [];
  for (const rule of config.scoring.dq_rules) {
    if (matchGroup(rule.conditions, answers)) ids.push(rule.id);
  }
  return ids;
}

/** First tier whose [score_min, score_max] band contains the score; an
 *  omitted bound is open. Falls back to the first tier if none match. */
export function pickTier(tiers: Tier[], score: number): Tier {
  for (const tier of tiers) {
    const okMin = tier.score_min === undefined || score >= tier.score_min;
    const okMax = tier.score_max === undefined || score <= tier.score_max;
    if (okMin && okMax) return tier;
  }
  // tiers is guaranteed non-empty by the schema (minItems: 1).
  return tiers[0]!;
}

/** Resolve the final outcome after the funnel completes. */
export function resolveOutcome(config: FunnelConfig, answers: Answers): Outcome {
  const score = computeScore(config, answers);

  if (config.scoring.enabled) {
    const dqReasons = collectDqReasons(config, answers);
    if (dqReasons.length > 0) {
      const dqRuleIds = collectDqRuleIds(config, answers);
      return {
        tier_id: DISQUALIFIED,
        qualified: false,
        score,
        dq_reasons: dqReasons,
        dq_rule_ids: dqRuleIds,
      };
    }
  }

  const tier = pickTier(config.scoring.tiers, score);
  return { tier_id: tier.id, qualified: true, score, dq_reasons: [], dq_rule_ids: [] };
}
